import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ethers } from 'ethers';
import { useWallet } from '../context/WalletContextV2';
import { getPool, addPoolLiquidity, removePoolLiquidity } from '../services/api';
import { web3Service, CONTRACT_ADDRESSES, ROUTER_ABI, ERC20_ABI, BLOKISTA_NETWORK } from '../services/web3';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import {
  ArrowLeft,
  Plus,
  Minus,
  Loader2,
  Check,
  AlertTriangle,
  Wallet,
  Lock,
  ExternalLink
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { VerifiedBadge } from '../components/VerifiedBadge';

// Deeply unwrap a JSON-RPC / ethers error to find the real revert reason.
const extractRpcError = (err) => {
  if (!err) return 'Unknown error';

  // Wallet-level authorization errors (most common: MetaMask 4100 when the
  // dapp tries to sign from an account the user didn't authorize this
  // session). Surface a clear, actionable hint instead of the raw message.
  const code = err?.code ?? err?.error?.code ?? err?.data?.code;
  if (code === 4100) {
    return 'Your wallet has not authorized this account for BlokSwap. Open MetaMask → click the site connection icon → make sure the account you want to use is connected, then try again.';
  }
  if (code === 4001 || code === 'ACTION_REJECTED') {
    return 'Transaction rejected in wallet.';
  }

  // MetaMask often wraps the real reason inside a JSON-stringified `stack`.
  // Try to parse it to reach the inner `originalError.message`.
  let parsedStack = null;
  try {
    if (typeof err.stack === 'string' && err.stack.trim().startsWith('{')) {
      parsedStack = JSON.parse(err.stack);
    }
  } catch {
    /* ignore */
  }

  const candidates = [
    err?.reason,
    err?.error?.error?.data?.originalError?.message,
    err?.error?.error?.data?.message,
    err?.error?.error?.message,
    err?.error?.data?.originalError?.message,
    err?.error?.data?.message,
    err?.error?.message,
    err?.data?.originalError?.message,
    err?.data?.message,
    parsedStack?.data?.originalError?.message,
    parsedStack?.data?.message,
    parsedStack?.message,
    err?.message,
  ];

  for (const c of candidates) {
    if (
      typeof c === 'string' &&
      c.trim() &&
      !c.toLowerCase().includes('internal json-rpc')
    ) {
      // Strip the noisy "execution reverted: " prefix if present
      return c.replace(/^execution reverted:?\s*/i, '').trim();
    }
  }
  return err.message || 'Transaction failed';
};

// Helper to display BCC instead of WBCC
const getDisplayToken = (token) => {
  if (token.symbol === 'WBCC') {
    return {
      ...token,
      symbol: 'BCC',
      name: 'Blokista',
      logo: 'https://api.dicebear.com/7.x/shapes/svg?seed=bcc&backgroundColor=DAA520'
    };
  }
  return token;
};

const AddLiquidityPage = () => {
  const { poolId } = useParams();
  const { isConnected, connectWallet, isConnecting, address, loadBalances, connector } = useWallet();
  
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('add');
  const [amount0, setAmount0] = useState('');
  const [amount1, setAmount1] = useState('');
  const [removePercent, setRemovePercent] = useState([50]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [txHash, setTxHash] = useState(null);
  const [needsApproval0, setNeedsApproval0] = useState(false);
  const [needsApproval1, setNeedsApproval1] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [balance0, setBalance0] = useState(0);
  const [balance1, setBalance1] = useState(0);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [chainReserves, setChainReserves] = useState({ r0: 0, r1: 0 }); // human-readable

  // Read on-chain reserves for the pool's pair (token-order aware)
  const fetchChainReserves = async () => {
    if (!pool || !pool.pair_address) return;
    try {
      const rpc = new ethers.providers.JsonRpcProvider(BLOKISTA_NETWORK.rpcUrls[0]);
      const pairC = new ethers.Contract(
        pool.pair_address,
        [
          'function getReserves() view returns (uint112,uint112,uint32)',
          'function token0() view returns (address)',
        ],
        rpc
      );
      const [r, t0] = await Promise.all([pairC.getReserves(), pairC.token0()]);
      const t0Lower = t0.toLowerCase();
      const pool0Lower = (pool.token0.isNative
        ? CONTRACT_ADDRESSES.WBCC
        : pool.token0.address).toLowerCase();
      const sameOrder = t0Lower === pool0Lower;
      const r0Raw = sameOrder ? r[0] : r[1];
      const r1Raw = sameOrder ? r[1] : r[0];
      const dec0 = pool.token0.decimals || 18;
      const dec1 = pool.token1.decimals || 18;
      setChainReserves({
        r0: parseFloat(ethers.utils.formatUnits(r0Raw, dec0)),
        r1: parseFloat(ethers.utils.formatUnits(r1Raw, dec1)),
        r0Raw,
        r1Raw,
      });
    } catch (err) {
      console.error('Failed to read on-chain reserves:', err);
    }
  };

  useEffect(() => {
    fetchChainReserves();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool?.id]);

  // Check if current user is the pool creator
  const isCreator = pool && address && 
    pool.creatorAddress?.toLowerCase() === address.toLowerCase();

  // Load pool data. Normalize WBCC-side tokens to native BCC so that
  // balance fetching, approvals, and addLiquidityETH all work consistently
  // with the "BCC" label shown in the UI.
  useEffect(() => {
    const normalizeToken = (token) => {
      if (!token) return token;
      if (token.address && token.address.toLowerCase() === CONTRACT_ADDRESSES.WBCC.toLowerCase()) {
        return {
          ...token,
          isNative: true,
          is_native: true,
          symbol: 'BCC',
          name: 'Blokista',
          logo: 'https://api.dicebear.com/7.x/shapes/svg?seed=bcc&backgroundColor=DAA520',
        };
      }
      return token;
    };

    const loadPool = async () => {
      try {
        const fetchedPool = await getPool(poolId);
        setPool({
          ...fetchedPool,
          token0: normalizeToken(fetchedPool.token0),
          token1: normalizeToken(fetchedPool.token1),
        });
      } catch (error) {
        console.error('Error loading pool:', error);
      }
      setLoading(false);
    };
    loadPool();
  }, [poolId]);

  // Fetch on-chain balances for the pool's actual tokens
  const refreshBalances = async () => {
    if (!isConnected || !address || !pool) return;
    setBalancesLoading(true);
    try {
      // Dedicated Blokista RPC provider — works even if wallet is on wrong network
      const rpc = new ethers.providers.JsonRpcProvider(BLOKISTA_NETWORK.rpcUrls[0]);

      const fetchTokenBalance = async (token) => {
        if (!token) return 0;
        try {
          // Pool's token is WBCC but the UI represents it as native BCC and
          // addLiquidityETH uses native BCC. So fetch native balance when the
          // pool token is either native OR the WBCC contract.
          const isWbcc =
            token.address &&
            token.address.toLowerCase() === CONTRACT_ADDRESSES.WBCC.toLowerCase();
          if (token.isNative || token.is_native || isWbcc) {
            const raw = await rpc.getBalance(address);
            return parseFloat(ethers.utils.formatEther(raw)) || 0;
          }
          if (!token.address || token.address === '0x0000000000000000000000000000000000000000') {
            return 0;
          }
          const contract = new ethers.Contract(token.address, ERC20_ABI, rpc);
          const raw = await contract.balanceOf(address);
          const decimals = token.decimals || 18;
          return parseFloat(ethers.utils.formatUnits(raw, decimals)) || 0;
        } catch (err) {
          console.error(`Failed to fetch balance for ${token.symbol} (${token.address}):`, err);
          return 0;
        }
      };

      const [b0, b1] = await Promise.all([
        fetchTokenBalance(pool.token0),
        fetchTokenBalance(pool.token1),
      ]);
      setBalance0(b0);
      setBalance1(b1);

      // Keep global balances in sync for header/other pages
      if (loadBalances) {
        loadBalances(address, [
          { id: pool.token0.id, address: pool.token0.address, isNative: pool.token0.isNative, decimals: pool.token0.decimals },
          { id: pool.token1.id, address: pool.token1.address, isNative: pool.token1.isNative, decimals: pool.token1.decimals },
        ]);
      }
    } catch (err) {
      console.error('Error loading pool balances:', err);
    }
    setBalancesLoading(false);
  };

  useEffect(() => {
    refreshBalances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, address, pool?.id]);

  // Check approvals when amounts change
  useEffect(() => {
    const checkApprovals = async () => {
      if (!isConnected || !pool || !amount0 || !amount1) return;
      
      try {
        // Check token0 approval (skip if native)
        if (!pool.token0.isNative && pool.token0.address !== '0x0000000000000000000000000000000000000000') {
          const allowance0 = await web3Service.checkAllowance(
            pool.token0.address,
            CONTRACT_ADDRESSES.ROUTER,
            address
          );
          setNeedsApproval0(parseFloat(allowance0) < parseFloat(amount0));
        }
        
        // Check token1 approval (skip if native)
        if (!pool.token1.isNative && pool.token1.address !== '0x0000000000000000000000000000000000000000') {
          const allowance1 = await web3Service.checkAllowance(
            pool.token1.address,
            CONTRACT_ADDRESSES.ROUTER,
            address
          );
          setNeedsApproval1(parseFloat(allowance1) < parseFloat(amount1));
        }
      } catch (error) {
        console.error('Error checking approvals:', error);
      }
    };
    
    checkApprovals();
  }, [isConnected, pool, amount0, amount1, address]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0d0d0d] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-yellow-400 animate-spin" />
      </div>
    );
  }

  if (!pool) {
    return (
      <div className="min-h-[calc(100vh-64px)] bg-[#0d0d0d] flex flex-col items-center justify-center">
        <p className="text-gray-400 mb-4">Pool not found</p>
        <Link to="/pools">
          <Button variant="outline">Back to Pools</Button>
        </Link>
      </div>
    );
  }

  // Display tokens (show BCC instead of WBCC)
  const displayToken0 = getDisplayToken(pool.token0);
  const displayToken1 = getDisplayToken(pool.token1);

  // Use on-chain reserves to derive the correct ratio. Fall back to DB price
  // only if reserves exist there. If the pool is empty (first liquidity),
  // there is NO ratio yet — users can freely choose both amounts.
  const hasReserves = chainReserves.r0 > 0 && chainReserves.r1 > 0;
  const reserveRatio = hasReserves
    ? chainReserves.r1 / chainReserves.r0
    : null;
  const currentPrice =
    reserveRatio !== null
      ? reserveRatio
      : pool.token1.price > 0 && pool.token0.price > 0
      ? pool.token0.price / pool.token1.price
      : null;

  // Auto-match the other amount ONLY if we actually know the ratio.
  const handleAmount0Change = (value) => {
    setAmount0(value);
    if (currentPrice && value && !isNaN(value)) {
      const calculated = parseFloat(value) * currentPrice;
      setAmount1(calculated.toFixed(6));
    }
  };

  const handleAmount1Change = (value) => {
    setAmount1(value);
    if (currentPrice && value && !isNaN(value)) {
      const calculated = parseFloat(value) / currentPrice;
      setAmount0(calculated.toFixed(6));
    }
  };

  // Check if user's inputs deviate too far from the locked pool ratio.
  // Returns null when ok, or a warning string when input is incompatible.
  const ratioMismatchWarning = (() => {
    if (!hasReserves) return null;
    const a0 = parseFloat(amount0);
    const a1 = parseFloat(amount1);
    if (!a0 || !a1) return null;
    const userRatio = a1 / a0;
    const poolRatio = reserveRatio;
    const drift = Math.abs(userRatio - poolRatio) / poolRatio;
    if (drift > 0.5) {
      return `Pool ratio is locked at 1 ${pool.token0.symbol} = ${poolRatio.toFixed(6)} ${pool.token1.symbol}. Your input implies a different price by ${(drift * 100).toFixed(1)}%. To add liquidity at the current ratio, match the auto-calculated amount. To change the price, use Swap first.`;
    }
    return null;
  })();

  const handleApprove = async (tokenIndex) => {
    if (!pool) return;
    
    setIsApproving(true);
    try {
      const tokenAddress = tokenIndex === 0 ? pool.token0.address : pool.token1.address;
      await web3Service.approveTokenMax(tokenAddress, CONTRACT_ADDRESSES.ROUTER);
      
      if (tokenIndex === 0) {
        setNeedsApproval0(false);
      } else {
        setNeedsApproval1(false);
      }
    } catch (error) {
      console.error('Approval failed (full error):', error);
      alert('Approval failed: ' + extractRpcError(error));
    }
    setIsApproving(false);
  };

  const handleAddLiquidity = async () => {
    if (!isConnected || !address || !isCreator) return;
    
    setIsSubmitting(true);
    setTxHash(null);
    
    try {
      // Pre-flight: make sure the wallet has the connected account
      // currently authorized for this dapp. If it's been deauthorized (or the
      // user switched accounts in MetaMask), MetaMask responds with 4100
      // when ethers asks it to sign. Detect and rebuild the signer here so
      // the user sees an actionable prompt instead of a cryptic error.
      try {
        let rawProvider = null;
        if (connector && typeof connector.getProvider === 'function') {
          try {
            rawProvider = await connector.getProvider();
          } catch { /* ignore */ }
        }
        if (!rawProvider && typeof window !== 'undefined' && window.ethereum) {
          rawProvider = window.ethereum;
        }
        if (rawProvider) {
          // eth_accounts returns the *currently authorized* accounts without
          // prompting. If empty, the dapp lost authorization and we must
          // re-request explicitly.
          let accounts = await rawProvider.request({ method: 'eth_accounts' });
          if (!accounts || accounts.length === 0) {
            accounts = await rawProvider.request({ method: 'eth_requestAccounts' });
          }
          const activeAddr = accounts && accounts[0];
          if (!activeAddr) {
            throw new Error('Wallet did not return any authorized account. Reconnect your wallet.');
          }

          // Rebuild the signer against the wallet's *current* primary account
          // so the `from` field we send actually matches MetaMask's view.
          const freshProvider = new ethers.providers.Web3Provider(rawProvider, 'any');
          const freshSigner = freshProvider.getSigner(activeAddr);
          web3Service.provider = freshProvider;
          web3Service.signer = freshSigner;
          web3Service.address = activeAddr;
          web3Service.isConnected = true;
          web3Service.initContracts();

          if (activeAddr.toLowerCase() !== address.toLowerCase()) {
            throw new Error(
              `Your wallet's active account (${activeAddr.slice(0, 8)}…) doesn't match the one you connected with (${address.slice(0, 8)}…). ` +
                `Switch back in MetaMask or reconnect.`
            );
          }
        }
      } catch (preflightErr) {
        // If the message already looks user-facing, surface it as-is.
        if (preflightErr?.message && !preflightErr?.code) {
          throw preflightErr;
        }
        throw preflightErr;
      }

      // Pre-flight validation
      const amt0 = parseFloat(amount0);
      const amt1 = parseFloat(amount1);
      if (!amt0 || !amt1 || amt0 <= 0 || amt1 <= 0) {
        throw new Error('Enter valid amounts for both tokens');
      }
      if (amt0 > balance0) {
        throw new Error(`Insufficient ${pool.token0.symbol} balance. You have ${balance0.toFixed(4)}, need ${amt0}`);
      }
      if (amt1 > balance1) {
        throw new Error(`Insufficient ${pool.token1.symbol} balance. You have ${balance1.toFixed(4)}, need ${amt1}`);
      }

      // Get token addresses
      const token0Addr = pool.token0.isNative ? CONTRACT_ADDRESSES.WBCC : pool.token0.address;
      const token1Addr = pool.token1.isNative ? CONTRACT_ADDRESSES.WBCC : pool.token1.address;
      
      const decimals0 = pool.token0.decimals || 18;
      const decimals1 = pool.token1.decimals || 18;
      
      const amount0Wei = ethers.utils.parseUnits(amount0, decimals0);
      const amount1Wei = ethers.utils.parseUnits(amount1, decimals1);
      
      // Calculate minimum amounts. The slippage tolerance must be high enough
      // to absorb both market drift AND any fee-on-transfer tax (some tokens
      // like the USDT on Blokista take 1-5% on transfer, which means the pair
      // receives less than amountDesired and the Router's strict >=
      // amountMin check trips with INSUFFICIENT_A_AMOUNT). 10% covers that
      // safely; the Router still uses the optimal amount from quote(), so
      // users don't lose value — this is just the lower bound.
      const SLIPPAGE_BPS = 1000; // 10%
      const amount0Min = amount0Wei.mul(10_000 - SLIPPAGE_BPS).div(10_000);
      const amount1Min = amount1Wei.mul(10_000 - SLIPPAGE_BPS).div(10_000);

      // Verify the pool's pair address actually matches what the Factory says.
      // If not, the DB row is stale/wrong (most common cause of phantom reverts).
      try {
        const rpc = new ethers.providers.JsonRpcProvider(BLOKISTA_NETWORK.rpcUrls[0]);
        const factoryC = new ethers.Contract(
          CONTRACT_ADDRESSES.FACTORY,
          ['function getPair(address,address) view returns (address)'],
          rpc
        );
        const chainPair = (await factoryC.getPair(token0Addr, token1Addr)).toLowerCase();
        if (chainPair === ethers.constants.AddressZero.toLowerCase()) {
          throw new Error(`No on-chain pair exists for ${pool.token0.symbol}/${pool.token1.symbol}. The token addresses in this pool may be wrong.`);
        }
        if (pool.pair_address && pool.pair_address.toLowerCase() !== chainPair) {
          throw new Error(`Pool DB entry is stale: on-chain pair is ${chainPair} but DB has ${pool.pair_address}. Delete this pool and recreate it.`);
        }
      } catch (preErr) {
        if (preErr.message?.includes('pool') || preErr.message?.includes('pair')) {
          throw preErr;
        }
        // RPC failure — let the main flow handle it
      }

      // Pre-flight allowance check (non-native tokens only) — avoids cryptic revert
      if (!pool.token0.isNative) {
        const allow0 = await web3Service.checkAllowance(pool.token0.address, CONTRACT_ADDRESSES.ROUTER, address);
        if (parseFloat(allow0) < amt0) {
          throw new Error(`${pool.token0.symbol} not approved for Router. Click "Approve ${pool.token0.symbol}" first.`);
        }
      }
      if (!pool.token1.isNative) {
        const allow1 = await web3Service.checkAllowance(pool.token1.address, CONTRACT_ADDRESSES.ROUTER, address);
        if (parseFloat(allow1) < amt1) {
          throw new Error(`${pool.token1.symbol} not approved for Router. Click "Approve ${pool.token1.symbol}" first.`);
        }
      }
      
      let result;
      
      if (pool.token0.isNative || pool.token1.isNative) {
        // Use addLiquidityETH for native token pairs
        const tokenAddr = pool.token0.isNative ? token1Addr : token0Addr;
        const tokenAmount = pool.token0.isNative ? amount1Wei : amount0Wei;
        const tokenMin = pool.token0.isNative ? amount1Min : amount0Min;
        const ethAmount = pool.token0.isNative ? amount0Wei : amount1Wei;
        const ethMin = pool.token0.isNative ? amount0Min : amount1Min;

        // Dry-run first to surface the real revert reason (if any)
        try {
          await web3Service.simulateAddLiquidityETH(
            tokenAddr, tokenAmount, tokenMin, ethMin, ethAmount
          );
        } catch (simErr) {
          console.error('Simulate addLiquidityETH reverted:', simErr);
          throw simErr;
        }

        result = await web3Service.addLiquidityETH(
          tokenAddr,
          tokenAmount,
          tokenMin,
          ethMin,
          ethAmount
        );
      } else {
        // Dry-run first to surface the real revert reason (if any)
        try {
          await web3Service.simulateAddLiquidity(
            token0Addr, token1Addr, amount0Wei, amount1Wei, amount0Min, amount1Min
          );
        } catch (simErr) {
          console.error('Simulate addLiquidity reverted:', simErr);
          throw simErr;
        }

        // Use addLiquidity for token-token pairs
        result = await web3Service.addLiquidity(
          token0Addr,
          token1Addr,
          amount0Wei,
          amount1Wei,
          amount0Min,
          amount1Min
        );
      }
      
      setTxHash(result.hash);
      
      // Update backend
      await addPoolLiquidity(
        pool.id,
        address,
        parseFloat(amount0),
        parseFloat(amount1),
        result.hash
      );
      
      setSubmitSuccess(true);
      // Refresh on-chain balances after liquidity added
      refreshBalances();
      setTimeout(() => {
        setSubmitSuccess(false);
        setAmount0('');
        setAmount1('');
        setTxHash(null);
      }, 5000);
    } catch (error) {
      console.error('Add liquidity failed (full error):', error);
      const msg = extractRpcError(error);
      alert('Failed to add liquidity: ' + msg);
    }
    setIsSubmitting(false);
  };

  const handleRemoveLiquidity = async () => {
    if (!isConnected || !address || !isCreator) return;
    
    setIsSubmitting(true);
    setTxHash(null);
    
    try {
      const percent = removePercent[0];
      
      // Get LP token balance
      if (pool.pairAddress) {
        const lpBalance = await web3Service.getLPBalance(pool.pairAddress, address);
        const lpToRemove = lpBalance.mul(percent).div(100);
        
        // Approve LP token spending if needed
        const lpAllowance = await web3Service.checkAllowance(
          pool.pairAddress,
          CONTRACT_ADDRESSES.ROUTER,
          address
        );
        
        if (parseFloat(lpAllowance) < parseFloat(ethers.utils.formatEther(lpToRemove))) {
          await web3Service.approveTokenMax(pool.pairAddress, CONTRACT_ADDRESSES.ROUTER);
        }
        
        // Get token addresses
        const token0Addr = pool.token0.isNative ? CONTRACT_ADDRESSES.WBCC : pool.token0.address;
        const token1Addr = pool.token1.isNative ? CONTRACT_ADDRESSES.WBCC : pool.token1.address;
        
        let result;
        
        if (pool.token0.isNative || pool.token1.isNative) {
          const tokenAddr = pool.token0.isNative ? token1Addr : token0Addr;
          result = await web3Service.removeLiquidityETH(
            tokenAddr,
            lpToRemove,
            0, // amountTokenMin (set to 0 for simplicity, should calculate properly)
            0  // amountETHMin
          );
        } else {
          result = await web3Service.removeLiquidity(
            token0Addr,
            token1Addr,
            lpToRemove,
            0,
            0
          );
        }
        
        setTxHash(result.hash);
        
        // Update backend
        await removePoolLiquidity(pool.id, address, percent, result.hash);
      } else {
        // No on-chain pair, just update backend
        await removePoolLiquidity(pool.id, address, percent);
      }
      
      setSubmitSuccess(true);
      // Refresh on-chain balances after liquidity removed
      refreshBalances();
      setTimeout(() => {
        setSubmitSuccess(false);
        setRemovePercent([50]);
        setTxHash(null);
      }, 5000);
    } catch (error) {
      console.error('Remove liquidity failed (full error):', error);
      alert('Failed to remove liquidity: ' + extractRpcError(error));
    }
    setIsSubmitting(false);
  };

  const insufficientBalance0 = parseFloat(amount0) > balance0;
  const insufficientBalance1 = parseFloat(amount1) > balance1;

  // Render not creator warning
  const renderNotCreatorWarning = () => (
    <div className="py-8 text-center space-y-4">
      <Lock className="w-16 h-16 mx-auto text-red-400" />
      <h3 className="text-xl font-bold text-white">Access Restricted</h3>
      <p className="text-gray-400 max-w-sm mx-auto">
        Only the pool creator can add or remove liquidity from this pool.
      </p>
      {pool.creatorAddress && (
        <div className="bg-white/5 rounded-xl p-4 max-w-sm mx-auto">
          <div className="text-sm text-gray-400 mb-1">Pool Creator</div>
          <a 
            href={`https://bccscan.com/address/${pool.creatorAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-yellow-400 hover:text-amber-300 flex items-center justify-center gap-1"
          >
            {pool.creatorAddress.slice(0, 10)}...{pool.creatorAddress.slice(-8)}
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
      <Link to="/pools">
        <Button variant="outline" className="mt-4">
          Back to Pools
        </Button>
      </Link>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#0d0d0d] relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-yellow-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-20 right-1/4 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Link
          to="/pools"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Pools
        </Link>

        {/* Pool Info Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex -space-x-2">
            <img
              src={displayToken0.logo}
              alt={displayToken0.symbol}
              className="w-10 h-10 rounded-full border-2 border-[#0d0d0d]"
            />
            <img
              src={displayToken1.logo}
              alt={displayToken1.symbol}
              className="w-10 h-10 rounded-full border-2 border-[#0d0d0d]"
            />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              {displayToken0.symbol}/{displayToken1.symbol}
              {pool.token0.verified && pool.token1.verified && <VerifiedBadge size={18} title="Verified token pair" />}
            </h1>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span className="px-2 py-0.5 rounded bg-white/10">{pool.fee}% fee</span>
              <span>TVL: <span className="text-green-400">${pool.tvl?.toFixed(2) || '0.00'}</span></span>
            </div>
          </div>
        </div>

        {/* Creator Badge */}
        {isConnected && (
          <div className={`mb-4 p-3 rounded-xl ${isCreator ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            <div className="flex items-center gap-2">
              {isCreator ? (
                <>
                  <Check className="w-5 h-5 text-green-400" />
                  <span className="text-green-200 text-sm">You are the pool creator - you can manage liquidity</span>
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 text-red-400" />
                  <span className="text-red-200 text-sm">Only the pool creator can manage liquidity</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Main Card */}
        <Card className="bg-[#1a1a1a] border-white/5 rounded-2xl overflow-hidden">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full bg-transparent border-b border-white/5 rounded-none p-0">
              <TabsTrigger
                value="add"
                className="flex-1 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-amber-500 data-[state=active]:text-yellow-400 data-[state=active]:bg-transparent"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Liquidity
              </TabsTrigger>
              <TabsTrigger
                value="remove"
                className="flex-1 py-4 rounded-none border-b-2 border-transparent data-[state=active]:border-amber-500 data-[state=active]:text-yellow-400 data-[state=active]:bg-transparent"
              >
                <Minus className="w-4 h-4 mr-2" />
                Remove Liquidity
              </TabsTrigger>
            </TabsList>

            {/* Add Liquidity Tab */}
            <TabsContent value="add" className="p-6 space-y-6">
              {!isConnected ? (
                <div className="py-8 text-center">
                  <Wallet className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400 mb-4">Connect your wallet to add liquidity</p>
                  <Button
                    onClick={connectWallet}
                    disabled={isConnecting}
                    className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold rounded-xl"
                  >
                    {isConnecting ? 'Connecting...' : 'Connect Wallet'}
                  </Button>
                </div>
              ) : !isCreator ? (
                renderNotCreatorWarning()
              ) : (
                <>
                  {/* Token 0 Input */}
                  <div className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">Deposit {displayToken0.symbol}</span>
                      <button
                        onClick={() => handleAmount0Change(balance0.toString())}
                        className="text-sm text-gray-400 hover:text-yellow-400 transition-colors"
                      >
                        Balance: {balance0.toFixed(4)}
                        <span className="ml-1 text-yellow-400">MAX</span>
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        value={amount0}
                        onChange={(e) => handleAmount0Change(e.target.value)}
                        placeholder="0"
                        className="flex-1 bg-transparent border-none text-2xl font-medium text-white placeholder:text-gray-600 focus-visible:ring-0 p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10">
                        <img src={displayToken0.logo} alt={displayToken0.symbol} className="w-6 h-6 rounded-full" />
                        <span className="font-semibold text-white">{displayToken0.symbol}</span>
                      </div>
                    </div>
                    {amount0 && (
                      <div className="mt-2 text-sm text-gray-500">
                        ~${(parseFloat(amount0) * pool.token0.price || 0).toFixed(2)}
                      </div>
                    )}
                  </div>

                  {/* Plus Icon */}
                  <div className="flex justify-center">
                    <div className="p-2 rounded-xl bg-white/5">
                      <Plus className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Token 1 Input */}
                  <div className="bg-white/5 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">Deposit {displayToken1.symbol}</span>
                      <button
                        onClick={() => handleAmount1Change(balance1.toString())}
                        className="text-sm text-gray-400 hover:text-yellow-400 transition-colors"
                      >
                        Balance: {balance1.toFixed(4)}
                        <span className="ml-1 text-yellow-400">MAX</span>
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        value={amount1}
                        onChange={(e) => handleAmount1Change(e.target.value)}
                        placeholder="0"
                        className="flex-1 bg-transparent border-none text-2xl font-medium text-white placeholder:text-gray-600 focus-visible:ring-0 p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/10">
                        <img src={displayToken1.logo} alt={displayToken1.symbol} className="w-6 h-6 rounded-full" />
                        <span className="font-semibold text-white">{displayToken1.symbol}</span>
                      </div>
                    </div>
                    {amount1 && (
                      <div className="mt-2 text-sm text-gray-500">
                        ~${(parseFloat(amount1) * pool.token1.price || 0).toFixed(2)}
                      </div>
                    )}
                  </div>

                  {/* Transaction Hash */}
                  {txHash && (
                    <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/30">
                      <div className="text-sm text-blue-200">
                        <strong>Transaction:</strong>{' '}
                        <a 
                          href={`https://bccscan.com/tx/${txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 underline"
                        >
                          {txHash.slice(0, 20)}...
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Ratio mismatch warning */}
                  {ratioMismatchWarning && (
                    <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-sm flex gap-2" data-testid="ratio-warning">
                      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                      <span>{ratioMismatchWarning}</span>
                    </div>
                  )}

                  {/* Approval Buttons */}
                  {needsApproval0 && (
                    <Button
                      onClick={() => handleApprove(0)}
                      disabled={isApproving}
                      className="w-full py-4 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded-xl"
                    >
                      {isApproving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Approve {displayToken0.symbol}
                    </Button>
                  )}
                  
                  {needsApproval1 && (
                    <Button
                      onClick={() => handleApprove(1)}
                      disabled={isApproving}
                      className="w-full py-4 bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 rounded-xl"
                    >
                      {isApproving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Approve {displayToken1.symbol}
                    </Button>
                  )}

                  {/* Submit Button */}
                  <Button
                    onClick={handleAddLiquidity}
                    disabled={
                      !amount0 ||
                      !amount1 ||
                      insufficientBalance0 ||
                      insufficientBalance1 ||
                      needsApproval0 ||
                      needsApproval1 ||
                      isSubmitting ||
                      submitSuccess ||
                      !!ratioMismatchWarning
                    }
                    className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-amber-400 hover:to-orange-400 text-black rounded-xl disabled:opacity-50"
                  >
                    {submitSuccess ? (
                      <><Check className="w-5 h-5 mr-2" /> Liquidity Added!</>
                    ) : isSubmitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Adding Liquidity...</>
                    ) : insufficientBalance0 || insufficientBalance1 ? (
                      <><AlertTriangle className="w-5 h-5 mr-2" /> Insufficient Balance</>
                    ) : !amount0 || !amount1 ? (
                      'Enter amounts'
                    ) : (
                      'Add Liquidity'
                    )}
                  </Button>
                </>
              )}
            </TabsContent>

            {/* Remove Liquidity Tab */}
            <TabsContent value="remove" className="p-6 space-y-6">
              {!isConnected ? (
                <div className="py-8 text-center">
                  <Wallet className="w-12 h-12 mx-auto mb-4 text-gray-500" />
                  <p className="text-gray-400 mb-4">Connect your wallet to remove liquidity</p>
                  <Button
                    onClick={connectWallet}
                    disabled={isConnecting}
                    className="bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-amber-400 hover:to-orange-400 text-black font-semibold rounded-xl"
                  >
                    {isConnecting ? 'Connecting...' : 'Connect Wallet'}
                  </Button>
                </div>
              ) : !isCreator ? (
                renderNotCreatorWarning()
              ) : (
                <>
                  {/* Amount to Remove */}
                  <div className="space-y-4">
                    <label className="text-sm text-gray-400">Amount to Remove</label>
                    <div className="text-center">
                      <span className="text-5xl font-bold text-white">{removePercent[0]}%</span>
                    </div>
                    <Slider
                      value={removePercent}
                      onValueChange={setRemovePercent}
                      max={100}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between gap-2">
                      {[25, 50, 75, 100].map((value) => (
                        <button
                          key={value}
                          onClick={() => setRemovePercent([value])}
                          className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                            removePercent[0] === value
                              ? 'bg-yellow-500 text-black'
                              : 'bg-white/5 text-white hover:bg-white/10'
                          }`}
                        >
                          {value}%
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="bg-white/5 rounded-xl p-4 space-y-3">
                    <div className="text-sm text-gray-400">You will receive (estimated)</div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src={displayToken0.logo} alt={displayToken0.symbol} className="w-6 h-6 rounded-full" />
                        <span className="text-white">{displayToken0.symbol}</span>
                      </div>
                      <span className="text-white font-medium">
                        {((pool.token0Reserve || 0) * removePercent[0] / 100).toFixed(4)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <img src={displayToken1.logo} alt={displayToken1.symbol} className="w-6 h-6 rounded-full" />
                        <span className="text-white">{displayToken1.symbol}</span>
                      </div>
                      <span className="text-white font-medium">
                        {((pool.token1Reserve || 0) * removePercent[0] / 100).toFixed(4)}
                      </span>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                    <div className="text-sm text-amber-200">
                      Tokens will be transferred to your wallet: {address?.slice(0, 10)}...
                    </div>
                  </div>

                  {/* Transaction Hash */}
                  {txHash && (
                    <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/30">
                      <div className="text-sm text-blue-200">
                        <strong>Transaction:</strong>{' '}
                        <a 
                          href={`https://bccscan.com/tx/${txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 underline"
                        >
                          {txHash.slice(0, 20)}...
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <Button
                    onClick={handleRemoveLiquidity}
                    disabled={removePercent[0] === 0 || isSubmitting || submitSuccess}
                    className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-400 hover:to-orange-400 text-white rounded-xl disabled:opacity-50"
                  >
                    {submitSuccess ? (
                      <><Check className="w-5 h-5 mr-2" /> Liquidity Removed!</>
                    ) : isSubmitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Removing Liquidity...</>
                    ) : (
                      'Remove Liquidity'
                    )}
                  </Button>
                </>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

export default AddLiquidityPage;
